/**
 * Notification translation helper.
 *
 * New notifications store `_titleKey`, `_bodyKey`, and `_params` in their `data`
 * JSON field. This helper resolves those keys against the current UI language,
 * falling back to the raw `title`/`body` columns for legacy notifications.
 *
 * For ADMIN_BROADCAST and INTERVIEW_SCHEDULED (custom-authored content):
 * - The title is translated (stored as an i18n key in _titleKey)
 * - The body is raw custom text (bodyKey is the raw string, not an i18n path)
 * - A translated "[Custom message]" label is prepended to mark it as custom
 */

type TranslationSource = Record<string, unknown>;

function deepGet(obj: TranslationSource, path: string): string | null {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur && typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[part];
    } else return null;
  }
  return typeof cur === 'string' ? cur : null;
}

function interpolate(template: string, params: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) => params[k] ?? `{{${k}}}`);
}

export interface TranslatedNotification {
  title: string;
  body: string;
  isCustomBody: boolean; // true = body is admin/recruiter custom text
}

/**
 * Resolve a notification's display text in the given language.
 *
 * @param n           - Raw notification object from the API
 * @param lang        - Current UI language code ('en' | 'pt')
 * @param translations - The full translations object for the current language
 * @param customLabel  - Translated label for custom messages, e.g. "Custom message"
 */
export function tn(
  n: { title: string; body: string; data?: Record<string, unknown> | null },
  lang: string,
  translations: TranslationSource,
  customLabel: string,
): TranslatedNotification {
  const d = (n.data ?? {}) as Record<string, unknown>;
  const titleKey = typeof d._titleKey === 'string' ? d._titleKey : null;
  const bodyKey  = typeof d._bodyKey  === 'string' ? d._bodyKey  : null;
  const params   = (typeof d._params === 'object' && d._params !== null)
    ? (d._params as Record<string, string>)
    : {};

  // ── Custom-body notifications (ADMIN_BROADCAST, INTERVIEW_SCHEDULED body) ─
  const isCustomTitle = titleKey && !titleKey.startsWith('notifications.');
  const isCustomBody  = bodyKey  && !bodyKey.startsWith('notifications.');

  // Title resolution
  let title = n.title; // raw fallback
  if (titleKey) {
    if (isCustomTitle) {
      title = titleKey; // raw custom title
    } else {
      const resolved = deepGet(translations, titleKey);
      if (resolved) title = interpolate(resolved, params);
    }
  }

  // Body resolution
  let body = n.body; // raw fallback
  let isCustomBodyFlag = false;
  if (bodyKey) {
    if (isCustomBody) {
      // Body is raw custom text — prefix with translated label
      body = n.body; // still use raw stored body
      isCustomBodyFlag = true;
    } else {
      const resolved = deepGet(translations, bodyKey);
      if (resolved) body = interpolate(resolved, params);
    }
  }

  return { title, body, isCustomBody: isCustomBodyFlag };
}

/**
 * Get the translated custom-message label from the translations.
 * Falls back to English if not present.
 */
export function getCustomLabel(translations: TranslationSource): string {
  const label = deepGet(translations, 'notifications._customLabel');
  return label ?? 'Custom message';
}
